import React from 'react';
import { Check, ChevronDown, ArrowRight } from 'lucide-react';
import { ORDER_TRACKING, badgeStyle } from '../../lib/statusConfig';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';

/**
 * Status change popover — badge-as-trigger pattern (Linear / Attio).
 *
 * Design goals (Phase 13.7):
 *   1. Zero layout shift — the popover is rendered via Radix Portal, outside
 *      the table DOM. Row height/width never change on hover/open.
 *   2. Discoverable — the badge visually cues that it's clickable via a soft
 *      hover state and a small chevron on hover.
 *   3. Fast — 1 click to open, 1 click to choose. Quick transitions
 *      (contextual next steps) are the largest, coloured targets at the top
 *      of the popover; "Tous les statuts" is a compact list below.
 *   4. Reuses the existing shadcn/ui `Popover` (Radix underneath).
 *   5. Terminal states (no_reply/returned/fake) hide the quick-actions block
 *      and show only the full status list.
 *
 * Behaviour:
 *   - Any click INSIDE the popover stops propagation so the row detail sheet
 *     doesn't open.
 *   - `onChange(newStatus)` fires exactly once per selection. The parent is
 *     responsible for the PATCH + toast.
 */

// Contextual quick transitions per current status.
const QUICK_TRANSITIONS = {
    nouveau: [
        { to: 'call_1_message_1', label: 'Call 1' },
        { to: 'confirmed',        label: 'Confirmer' },
        { to: 'fake',             label: 'Faux' },
    ],
    call_1_message_1: [
        { to: 'call_2_message_2', label: 'Call 2' },
        { to: 'confirmed',        label: 'Confirmer' },
        { to: 'no_reply',         label: 'Sans Réponse' },
    ],
    call_2_message_2: [
        { to: 'call_3',           label: 'Call 3' },
        { to: 'confirmed',        label: 'Confirmer' },
        { to: 'no_reply',         label: 'Sans Réponse' },
    ],
    call_3: [
        { to: 'confirmed',        label: 'Confirmer' },
        { to: 'no_reply',         label: 'Sans Réponse' },
    ],
    confirmed: [
        { to: 'in_delivery',      label: 'In Delivery' },
    ],
    in_delivery: [
        { to: 'returned',         label: 'Retourné' },
    ],
    no_reply: [],
    returned: [],
    fake: [],
};

// Order used for the "Tous les statuts" list (mirrors business flow).
const ALL_STATUSES_ORDER = [
    'nouveau',
    'call_1_message_1',
    'call_2_message_2',
    'call_3',
    'confirmed',
    'in_delivery',
    'no_reply',
    'returned',
    'fake',
];

export default function RowStatusActions({ value, onChange, disabled = false }) {
    const [open, setOpen] = React.useState(false);
    const currentCfg = ORDER_TRACKING[value] || { color: '#6B7280', label: value };
    const quick = QUICK_TRANSITIONS[value] || [];

    const pick = React.useCallback((nextStatus) => {
        setOpen(false);
        if (nextStatus === value) return;
        onChange(nextStatus);
    }, [onChange, value]);

    // Read-only rendering when disabled — just the badge, no interaction.
    if (disabled) {
        return (
            <span
                data-testid="row-status-badge"
                className="inline-flex items-center h-6 px-2 text-[11px] font-medium rounded-md whitespace-nowrap"
                style={badgeStyle(currentCfg.color)}
            >
                {currentCfg.label}
            </span>
        );
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid="row-status-badge"
                    data-status={value}
                    onClick={(e) => e.stopPropagation()}
                    className="group/badge inline-flex items-center gap-1 h-6 pl-2 pr-1.5 text-[11px] font-semibold rounded-md whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#4B3FD8] transition-[filter] hover:brightness-95"
                    style={badgeStyle(currentCfg.color)}
                    aria-label={`Changer le statut (actuel : ${currentCfg.label})`}
                >
                    <span>{currentCfg.label}</span>
                    <ChevronDown
                        size={11}
                        strokeWidth={2.25}
                        className="opacity-40 group-hover/badge:opacity-90 transition-opacity"
                    />
                </button>
            </PopoverTrigger>

            <PopoverContent
                data-testid="status-popover"
                align="start"
                sideOffset={6}
                className="w-[260px] p-0 bg-white border-[#E5E7EB] rounded-lg shadow-[0_8px_24px_rgba(16,24,40,0.12)]"
                onClick={(e) => e.stopPropagation()}
                onCloseAutoFocus={(e) => e.preventDefault()}
            >
                {/* Header — muted label + current badge for reassurance */}
                <div className="px-3 pt-3 pb-2">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1.5">
                        Statut actuel
                    </div>
                    <span
                        data-testid="status-popover-current"
                        className="inline-flex items-center h-6 px-2 text-[11px] font-medium rounded-md whitespace-nowrap"
                        style={badgeStyle(currentCfg.color)}
                    >
                        {currentCfg.label}
                    </span>
                </div>

                {/* Quick transitions (contextual) — shown only when the current
                    status has at least one canonical next step. */}
                {quick.length > 0 && (
                    <>
                        <div className="border-t border-[#F0F0F0]" />
                        <div className="px-2 py-2">
                            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280] px-1 pb-1.5">
                                Actions rapides
                            </div>
                            <div className="flex flex-col gap-1">
                                {quick.map((t) => {
                                    const cfg = ORDER_TRACKING[t.to];
                                    return (
                                        <button
                                            key={t.to}
                                            type="button"
                                            data-testid={`status-quick-${t.to}`}
                                            onClick={() => pick(t.to)}
                                            className="group/qi flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm text-[#1A1D2E] hover:bg-[#F5F6FA] transition-colors text-left"
                                        >
                                            <span
                                                className="inline-flex items-center h-6 px-2 text-[11px] font-semibold rounded-md whitespace-nowrap"
                                                style={badgeStyle(cfg.color)}
                                            >
                                                {cfg.label}
                                            </span>
                                            <ArrowRight
                                                size={13}
                                                strokeWidth={2}
                                                className="ml-auto text-[#6B7280] opacity-0 group-hover/qi:opacity-100 transition-opacity"
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}

                {/* All statuses — always available */}
                <div className="border-t border-[#F0F0F0]" />
                <div className="px-2 py-2">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280] px-1 pb-1.5">
                        Tous les statuts
                    </div>
                    <div className="flex flex-col">
                        {ALL_STATUSES_ORDER.map((s) => {
                            const cfg = ORDER_TRACKING[s];
                            const isCurrent = s === value;
                            return (
                                <button
                                    key={s}
                                    type="button"
                                    data-testid={`status-option-${s}`}
                                    onClick={() => pick(s)}
                                    disabled={isCurrent}
                                    className="flex items-center gap-2 w-full px-2 py-1 rounded-md text-sm text-[#1A1D2E] hover:bg-[#F5F6FA] transition-colors text-left disabled:cursor-default"
                                >
                                    <span
                                        className="inline-block w-2 h-2 rounded-full shrink-0"
                                        style={{ backgroundColor: cfg.color }}
                                    />
                                    <span className={isCurrent ? 'font-medium' : ''}>{cfg.label}</span>
                                    {isCurrent && (
                                        <Check size={13} strokeWidth={2.25} className="ml-auto text-[#4B3FD8]" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
